import { Component, DestroyRef, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { Accordion, AccordionTabCloseEvent } from 'primeng/accordion';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { firstValueFrom, take } from 'rxjs';
import Swal from 'sweetalert2';
import { UdlControlledVocabulary, ValueDisplayPair } from '../../../../shared/models/udl-controlled-vocabulary.model';
import { EsaUdlOverlapToolService } from '../../../../shared/services/esa-udl-overlap-tool.service';
import { UdlOverlapToolContainerService, SelectedFileNameKeys } from '../../../../esa-assessment/udl-overlap-tool/udl-overlap-tool-container.service';
import { EsaInputs } from '../../../../shared/models/esa-inputs';
import { Project } from '../../../../shared/models/project';
import { ProjectXEpaXModelInputsDTO } from '../../../../shared/models/project-x-epa-x-model-inputs-dto';
import { SelectedProjectApiService } from '../../../../shared/components/selected-project/selected-project.api.service';
import { EPAProjectInputsService } from '../esa-setup-inputs/esa-setup-inputs.api.service';
import { UdlModelService } from '../../udl-model.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UdlControlledVocabularyService } from 'src/app/esa-assessment/udl-overlap-tool/udl-controlled-vocabulary.service';
import makeDistinct from 'src/app/shared/utils/array-utils';
import { Constants } from 'src/app/shared/utils/constants';
import { SelectItem } from 'primeng/api';

@Component({
  selector: 'app-esa-setup-models-to-be-run',
  templateUrl: './esa-setup-models-to-be-run.component.html',
  styleUrls: ['./esa-setup-models-to-be-run.component.css']
})

export class ESASetupModelsToBeRunComponent {
  destroyRef = inject(DestroyRef)
  @ViewChild('accordion') accordion!: Accordion;

  @Input() selectedProject?: Project;
  @Input() canMakeEdits: boolean = false;
  @Input() projectPk: number = 0;
  public loading: boolean = false;
  @Output() areSetupModelsValidEvent = new EventEmitter<boolean>();

  activeIndex: number | null = null;

  modelHeader: string = "EPA UDL Overlap";
  chemicalName: string = '';
  isChemicalNameValid: boolean = false;

  selectedSpeciesDistribution?: UdlControlledVocabulary;
  isSpeciesDistributionValid: boolean = false;

  includeDriftOptions: ValueDisplayPair[] = [ { value: "Yes", display: 'Yes' }, { value: "No", display: 'No' } ];
  includeDrift: string = '';
  isIncludeDriftValid: boolean = true;

  regionsToIncludePlaceholder: string = 'Select Regions to Include';
  selectedRegionsToInclude?: UdlControlledVocabulary[] = [];
  isRegionsToIncludeValid: boolean = false;
  selectAllRegions: boolean = false;

  statesToIncludeLabel: string = 'States to Include*';
  statesToIncludePlaceholder: string = 'Select States to Include';
  selectedStatesToInclude?: UdlControlledVocabulary[] = [];
  isStatesToIncludeValid: boolean = false;
  isStatesToIncludeRequired: boolean = true;
  selectAllStates: boolean = false;

  bufferDistancesOptionsWithSettings: UdlControlledVocabulary[] = [];
  selectedBufferDistances: UdlControlledVocabulary[] = [];
  isBufferDistancesValid: boolean = false;
  selectAllBufferDistances: boolean = false;
  bufferDistancesDeselectableList: UdlControlledVocabulary[] = [];
  bufferDistancesPreSelectedList: UdlControlledVocabulary[] = [];

  onOffFieldConsiderationsPlaceholder: string = 'Select On/Off Field Considerations';
  selectedOnOffFieldConsiderations?: UdlControlledVocabulary[] = [];
  isOnOffFieldConsiderationsValid: boolean = false;
  selectAllOnOffFieldConsiderations: boolean = false;

  adjustForPCTOptions: ValueDisplayPair[] = [ { value: "Yes", display: 'Yes' }, { value: "No", display: 'No' } ];
  adjustForPCT: string = '';
  isAdjustForPCTValid: boolean = true;

  bufferDistanceInSummaryReportOptions: UdlControlledVocabulary[] = [];
  selectedBufferDistanceInSummaryReport?: UdlControlledVocabulary;
  isBufferDistanceInSummaryReportValid: boolean = false;

  isPCT: boolean = false;
  isPCTValuesFileSelected: boolean = false;
  isPCTValuesFileValid: boolean = false;
  isPCTFileNameContainSpaces: boolean = false;

  isCropUseValid: boolean = false;
  allCropUseOptions: string[] = [];
  cropUseOptions: string[] = [];
  formattedCropUseOptions: SelectItem[] = [];
  selectedCropUseValues: string[] = [];
  selectAllCropUseOptions: boolean = false;

  isOnAndOffConsiderationsFileSelected: boolean = false;
  isOnAndOffConsiderationsFileValid: boolean = false;
  isOnAndOffConsiderationsFileNameContainSpaces: boolean = false;

  isObligateAndIndirectRelationshipFileSelected: boolean = false;
  isObligateAndIndirectRelationshipFileValid: boolean = false;
  isObligateAndIndirectRelationshipFileNameContainSpaces: boolean = false;

  isFileInputTriggeredByDialog  = false;

  @ViewChild('pctValuesFileSelected') pctValuesFileInput!: ElementRef;
  @ViewChild('onAndOffConsiderationsFileSelected') onAndOffConsiderationsFileInput!: ElementRef;
  @ViewChild('obligateAndIndirectRelationshipFileSelected') obligateAndIndirectRelationshipFileInput!: ElementRef;

  uploadedPCTFilename: string = '';
  uploadedOnOffConsiderationsFilename: string = '';
  uploadedObligateAndIndirectRelationshipFilename: string = '';

  constructor(
    public udlOverlapToolContainerService: UdlOverlapToolContainerService,
    private epaProjectInputsService: EPAProjectInputsService,
    private selectedProjectApiService: SelectedProjectApiService,
    private modelService: UdlModelService,
    public controlledVocabularyService: UdlControlledVocabularyService
  ) {
      epaProjectInputsService.getAndSetInputsEvent.subscribe(async (projectPk: number) => {
        await this.getSavedModelInputs(projectPk);
      });

      udlOverlapToolContainerService.projectFileDeletedEvent.subscribe((fileKey: SelectedFileNameKeys) => {
        switch (fileKey) {
          case SelectedFileNameKeys.PCTValues:
            this.uploadedPCTFilename = '';
            this.isPCTValuesFileValid = false;
            break;
          case SelectedFileNameKeys.OnOffConsiderations:
            this.uploadedOnOffConsiderationsFilename = '';
            this.isOnAndOffConsiderationsFileValid = false;
            break;
          case SelectedFileNameKeys.ObligateAndIndirectRelationship:
            this.uploadedObligateAndIndirectRelationshipFilename = '';
            this.isObligateAndIndirectRelationshipFileValid = false;
            break;
        }
        this.validateModelInputs();
      });
    }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    await this.controlledVocabularyService.WaitTillLoaded();
    await this.getAllCropUseOptions();
    this.initializeDefaultModelInputs();

    this.areSetupModelsValidEvent.subscribe(async (valid: boolean) => {
      if (valid) {
          await this.setModelInputs();
        }
    });

    this.setSavedInputsOnSelectedProject();
  }

  onClose(event: AccordionTabCloseEvent) {
    this.activeIndex = null;
  }

  onOpen({ index }: AccordionTabCloseEvent) {
    this.activeIndex = index;
  }

  private async initializeDefaultModelInputs() {
    this.includeDrift = 'Yes';
    this.isIncludeDriftValid = true;
    this.adjustForPCT = 'No';
    this.isAdjustForPCTValid = true;
    this.isPCT = false;
    this.initializeControlsUdl();
  }

  private async resetDefaultModelInputs() {
    this.loading = true;

    this.initializeDefaultModelInputs();
    this.chemicalName = '';
    this.isChemicalNameValid = false;
    this.selectedOnOffFieldConsiderations = [];
    this.isOnOffFieldConsiderationsValid = false;
    this.selectAllRegions = false;
    this.selectAllBufferDistances = false;
    this.selectAllOnOffFieldConsiderations = false;
    this.isBufferDistanceInSummaryReportValid = false;
    this.selectedBufferDistanceInSummaryReport = undefined;
    this.isCropUseValid = false;
    this.pctValuesFileInput.nativeElement.value = null;
    this.isPCTValuesFileValid = false;
    this.onAndOffConsiderationsFileInput.nativeElement.value = null;
    this.isOnAndOffConsiderationsFileSelected = false;
    this.isOnAndOffConsiderationsFileValid = false;
    this.obligateAndIndirectRelationshipFileInput.nativeElement.value = null;
    this.isObligateAndIndirectRelationshipFileSelected = false;
    this.isObligateAndIndirectRelationshipFileValid = false;

    this.selectedCropUseValues = this.cropUseOptions;
    this.onCropUseChanged([]);
    this.uploadedPCTFilename = '';
    this.uploadedOnOffConsiderationsFilename = '';
    this.uploadedObligateAndIndirectRelationshipFilename = '';

    this.loading = false;
  }

  private initializeControlsUdl() {
    this.selectedSpeciesDistribution = this.controlledVocabularyService.speciesDistributionOptions?.find(x => x.long_value?.toLowerCase() == "range");
    this.isSpeciesDistributionValid = this.selectedSpeciesDistribution != null;

    this.selectedRegionsToInclude = this.controlledVocabularyService.regionsToIncludeOptions.filter(x => x.short_value?.toLowerCase() == "conus");
    this.isRegionsToIncludeValid = this.selectedRegionsToInclude?.length != 0;

    this.setupStatesToInclude();

    if (this.loading) {
      this.setBufferDistancesAttributes();
    }
    this.selectedBufferDistances = this.bufferDistancesPreSelectedList;
    this.isBufferDistancesValid = this.selectedBufferDistances?.length != 0;

    this.setBufferDistanceInSummaryReportOptions();
    this.setCropUseOptions();
  }

  getOptionsByModel(modelHeader: string, controlledVocabulary: UdlControlledVocabulary[]) {
    const optionsForModel: UdlControlledVocabulary[] = [];

    controlledVocabulary.forEach(CVItem => {
      if (CVItem.metadata) {
        const jsonObject = JSON.parse(CVItem.metadata);
        if (jsonObject.versions && Array.isArray(jsonObject.versions)) {
          if (jsonObject.versions.includes(modelHeader)) {
            optionsForModel.push(CVItem);
          }
        }
      }
    });

    return optionsForModel;
  }

  private async getAllCropUseOptions() {
    this.allCropUseOptions = await firstValueFrom(this.epaProjectInputsService.getCropUseValuesForRegions());
  }

  onCropUseChanged(values: string[]) {
    this.selectedCropUseValues = values;
    this.validateCropUse();
    this.validateModelInputs();
    this.selectAllCropUseOptions = this.selectedCropUseValues?.length == this.cropUseOptions.length;
  }

  validateCropUse() {
    const regionsSelected = this.selectedRegionsToInclude?.map(region => region.short_value) || [];

    if (this.selectedCropUseValues) { // needed for old projects where crop use was a file uploaded, not a multiselect
      const cropUseRegions = [...new Set(this.selectedCropUseValues.map(value => value.split('_')[0]))];
      this.isCropUseValid = regionsSelected.every(region => cropUseRegions.some(value => value == region));
    }
  }

  selectAllCropUseChanged(event: any) {
    this.selectAllCropUseOptions = event.checked;
    this.onCropUseChanged(this.selectAllCropUseOptions ? this.cropUseOptions : []);
  }

  private setCropUseOptions() {
    this.cropUseOptions = this.allCropUseOptions.filter(cropUseOption => this.selectedRegionsToInclude?.some(region => cropUseOption.startsWith(region.short_value??"")));
    this.cropUseOptions.sort((a, b) => {
      const isAConus = a.startsWith('CONUS');
      const isBConus = b.startsWith('CONUS');
      if (isAConus && !isBConus) return -1;
      if (!isAConus && isBConus) return 1;
      return a.localeCompare(b);
    });
    this.selectedCropUseValues = this.selectedCropUseValues.filter(value => this.cropUseOptions.includes(value));
    this.selectAllCropUseOptions = this.selectedCropUseValues?.length == this.cropUseOptions.length;
    this.formattedCropUseOptions = this.cropUseOptions.map(o => ({ label: o, value: o }));
  }

  setBufferDistancesAttributes() {
    this.bufferDistancesOptionsWithSettings = this.controlledVocabularyService.bufferDistancesOptions.map(item => {
      if (item.metadata) {
        const jsonObject = JSON.parse(item.metadata);
        if (jsonObject.bufferDistancesAttributes) {
          if (jsonObject.bufferDistancesAttributes.isDeselectable) {
            item.disabled = false;
          }
          else {
            item.disabled = true; // Business requirement: Zero will be disabled to deselect.
          }
          if (jsonObject.bufferDistancesAttributes.isPreSelected) {
            this.bufferDistancesPreSelectedList.push(item)
          }
        }
      }
      return item;
    });
  }

  async chemicalNameChanged(value: string) {
    this.isChemicalNameValid = value.length > 0;
    this.chemicalName = value;

    this.validateModelInputs();
  }

  speciesDistributionSelectionChanged( value : UdlControlledVocabulary | undefined) {
    this.isSpeciesDistributionValid = value != null;
    this.selectedSpeciesDistribution = value;

    this.validateModelInputs();
  }

  includeDriftSelectionChanged({ value }: DropdownChangeEvent) {
    this.isIncludeDriftValid = value != null;
    this.includeDrift = value;

    this.validateModelInputs();
  }

  regionsToIncludeSelectionChanged(value: UdlControlledVocabulary[]) {
    this.selectedRegionsToInclude = value;
    this.isRegionsToIncludeValid = this.selectedRegionsToInclude?.length != 0;
    this.selectAllRegions = this.selectedRegionsToInclude?.length == this.controlledVocabularyService.regionsToIncludeOptions.length;
    this.setupStatesToInclude();
    this.setCropUseOptions();
    this.validateModelInputs();
  }

  regionsToIncludeSelectAllChanged(event: any) {
    this.selectAllRegions = event.checked;
    this.selectedRegionsToInclude = this.selectAllRegions ? this.controlledVocabularyService.regionsToIncludeOptions : [];
    this.isRegionsToIncludeValid = this.selectedRegionsToInclude?.length != 0;
    this.setupStatesToInclude();
    this.setCropUseOptions();
    this.validateModelInputs();
  }

  statesToIncludeSelectionChanged(event: any) {
    this.selectedStatesToInclude = event.value;
    this.isStatesToIncludeValid = this.selectedStatesToInclude?.length != 0;
    this.selectAllStates = this.selectedStatesToInclude?.length == this.controlledVocabularyService.statesToIncludeOptions.length;

    this.validateModelInputs();
  }

  statesToIncludeSelectAllChanged(event: any) {
    this.selectAllStates= event.checked;
    this.selectedStatesToInclude = this.selectAllStates ? this.controlledVocabularyService.statesToIncludeOptions : [];
    this.isStatesToIncludeValid = this.selectedStatesToInclude?.length != 0;

    this.validateModelInputs();
  }

  bufferDistancesChanged(value: UdlControlledVocabulary[]) {


    this.selectedBufferDistances = value;
    this.isBufferDistancesValid = this.selectedBufferDistances?.length != 0;
    this.selectAllBufferDistances = this.selectedBufferDistances?.length == this.bufferDistancesOptionsWithSettings.length;

    this.setBufferDistanceInSummaryReportOptions();
    this.validateModelInputs();
  }

  bufferDistancesSelectAllChanged(event: any) {
    this.selectAllBufferDistances= event.checked;
    this.selectedBufferDistances = this.selectAllBufferDistances ? this.bufferDistancesOptionsWithSettings : this.bufferDistancesOptionsWithSettings.filter(x =>x.disabled); // Business requirement: Zero will be disabled to deselect.
    this.isBufferDistancesValid = this.selectedBufferDistances?.length != 0;

    this.setBufferDistanceInSummaryReportOptions();
    this.validateModelInputs();
  }

  onOffFieldConsiderationsChanged(value: UdlControlledVocabulary[]) {
    this.selectedOnOffFieldConsiderations = value;
    this.isOnOffFieldConsiderationsValid = this.selectedOnOffFieldConsiderations?.length != 0;
    this.selectAllOnOffFieldConsiderations = this.selectedOnOffFieldConsiderations?.length == this.controlledVocabularyService.onOffFieldConsiderationsOptions.length;

    this.validateModelInputs();
  }

  onOffFieldConsiderationsSelectAllChanged(event: any) {
    this.selectAllOnOffFieldConsiderations= event.checked;
    this.selectedOnOffFieldConsiderations = this.selectAllOnOffFieldConsiderations ? this.controlledVocabularyService.onOffFieldConsiderationsOptions : [];
    this.isOnOffFieldConsiderationsValid = this.selectedOnOffFieldConsiderations?.length != 0;

    this.validateModelInputs();
  }

  adjustForPCTSelectionChanged({ value }: DropdownChangeEvent) {
    if (value == Constants.AFFIRMATIVE_VALUES.NO) {
      this.pctValuesFileInput.nativeElement.value = null;
      this.uploadedPCTFilename = '';
      this.isPCTValuesFileValid = false;
      this.isPCTValuesFileSelected = false;
      if (this.selectedProject?.projectPk) this.udlOverlapToolContainerService.deleteUploadedFile(this.selectedProject.projectPk, SelectedFileNameKeys.PCTValues, true);
    }
    this.adjustForPCT = value;
    this.isAdjustForPCTValid = true;
    this.isPCT = value === Constants.AFFIRMATIVE_VALUES.NO ? false : true;

    this.validateModelInputs();
  }

  bufferDistanceInSummaryReportSelectionChanged(value: UdlControlledVocabulary | undefined) {
    this.isBufferDistanceInSummaryReportValid = value != null;
    this.selectedBufferDistanceInSummaryReport = value;

    this.validateModelInputs();
  }

  setBufferDistanceInSummaryReportOptions() {
    this.selectedBufferDistances = makeDistinct(this.selectedBufferDistances);

    this.bufferDistanceInSummaryReportOptions = this.selectedBufferDistances?.map(option => ({
      ...option,
      disabled: false
    })).sort((a, b) => (Number(a.short_value) || 0) - (Number(b.short_value) || 0));

    if (!this.bufferDistanceInSummaryReportOptions.find(x => x.short_value == this.selectedBufferDistanceInSummaryReport?.short_value)) {
      this.selectedBufferDistanceInSummaryReport = undefined;
      this.isBufferDistanceInSummaryReportValid = false;
    }
  }

  async downloadProjectFile(filename: string) {
    await this.udlOverlapToolContainerService.downloadProjectFile(this.projectPk, this.modelService.modelVersion, filename);;
  }

  public stringContainsSpaces(input: string) : boolean {
    return input.includes(' ');
  }

  async onFileInputClick(event: MouseEvent, sampleFileName: string): Promise<void> {
    if (this.isFileInputTriggeredByDialog ) {
      this.isFileInputTriggeredByDialog = false; // Reset to avoid the loop
      return;
    }

    event.preventDefault(); // Prevent the default file input behavior

    const udlAdministratorEmailLink = `<a href="mailto:${this.controlledVocabularyService.udlAdministrators}" style="color:#0069be; text-decoration:underline;">UDL Administrator</a>`;

    Swal.fire({
      html: `EPA\'s default ${sampleFileName} file will run automatically. If you need to update the file, please contact ${udlAdministratorEmailLink}.<br><br>Do you want to upload your own file?`,
      showCancelButton: true,
      confirmButtonColor: '#0069be',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      icon: 'warning'
    }).then((result) => {
      if (result.isConfirmed) {
        this.isFileInputTriggeredByDialog = true; // Set to prevent loop
        const inputElement = event.target as HTMLInputElement;
        inputElement?.click();
      }
    });
  }

  async onFileSelected(event: Event, fileExtension: string,
     isValidVariable: keyof ESASetupModelsToBeRunComponent,
     fileNameContainsSpacesVariable: keyof ESASetupModelsToBeRunComponent,
     isFileSelectedVariable: keyof ESASetupModelsToBeRunComponent,
     fileNameToMarkForDeletion: string): Promise<void> {

    const inputElement = event.target as HTMLInputElement;
    if (inputElement == null || inputElement.files == null) return;
    if (inputElement.files.length > 0) {
      const file = inputElement.files[0];

      (this[fileNameContainsSpacesVariable] as boolean) = this.stringContainsSpaces(file.name);

      if (this.stringContainsSpaces(file.name)) {
        inputElement.value = '';
        return;
      }

      (this[isValidVariable] as boolean) = (file.name.endsWith(fileExtension));
      (this[isFileSelectedVariable] as boolean) = true;

      await this.setFilesToBeUploaded();
    }
    else {
      (this[isValidVariable] as boolean) = false;
      (this[isFileSelectedVariable] as boolean) = false;
    }
    this.validateModelInputs();
  }

  setupStatesToInclude() {
    if (this.selectedRegionsToInclude?.some(x => x.short_value?.toLowerCase() == "conus")) {
      if (this.selectedStatesToInclude?.length == 0 || this.loading) {
        this.selectedStatesToInclude = this.controlledVocabularyService.statesToIncludeOptions;
        this.isStatesToIncludeValid = this.selectedStatesToInclude != null;
        this.isStatesToIncludeRequired = true;
        this.selectAllStates = true;
        this.statesToIncludeLabel = 'States to Include*';
      }
    }
    else {
      this.selectedStatesToInclude = [];
      this.isStatesToIncludeRequired = false;
      this.isStatesToIncludeValid = false;
      this.selectAllStates = false;
      this.statesToIncludeLabel = 'States to Include';
    }
  }

  clearModelInputs() {
    Swal.fire({
      title: 'Clear Model Inputs',
      text: 'Do you want clear model inputs?',
      showCancelButton: true,
      confirmButtonColor: '#0069be',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      icon: 'warning'
    }).then((result) => {
      if (result.value) {
        this.resetDefaultModelInputs();
      }
    });
  }

  validateModelInputs() {
    this.areSetupModelsValidEvent.emit(this.isChemicalNameValid &&
      this.isSpeciesDistributionValid &&
      this.isIncludeDriftValid &&
      this.isRegionsToIncludeValid &&
      (this.isStatesToIncludeRequired ? this.isStatesToIncludeValid : true) &&
      this.isBufferDistancesValid &&
      this.isOnOffFieldConsiderationsValid &&
      this.isAdjustForPCTValid &&
      this.isCropUseValid &&
      (this.isPCTValuesFileSelected ? this.isPCTValuesFileValid : true) &&
      (this.isOnAndOffConsiderationsFileSelected ? this.isOnAndOffConsiderationsFileValid : true) &&
      (this.isObligateAndIndirectRelationshipFileSelected ? this.isObligateAndIndirectRelationshipFileValid : true) &&
      this.isBufferDistanceInSummaryReportValid);
  }

  // set resetChangeDetection to true when we want to set the model inputs but not trigger the change detection
  async setModelInputs(resetChangeDetection: boolean = false) {
    var inputs : EsaInputs = {
      chemicalName: this.chemicalName,
      selectedSpeciesHabitat: this.selectedSpeciesDistribution!,
      includeDrift: this.includeDrift,
      selectedRegionsToInclude: this.selectedRegionsToInclude!,
      selectedStatesToInclude: this.selectedStatesToInclude!,
      selectedBufferDistances: this.selectedBufferDistances!,
      selectedOnOffFieldConsiderations: this.selectedOnOffFieldConsiderations!,
      adjustForPCT: this.adjustForPCT,
      selectedBufferDistanceInSummaryReport: this.selectedBufferDistanceInSummaryReport!,
      isPCT: this.isPCT,
      selectedCropUseValues: this.selectedCropUseValues,
      selectedPCTValuesFileName: this.uploadedPCTFilename && this.uploadedPCTFilename.length > 0 ? this.uploadedPCTFilename : this.pctValuesFileInput.nativeElement.files[0]?.name,
      selectedOnOffConsiderationsFileName: this.uploadedOnOffConsiderationsFilename && this.uploadedOnOffConsiderationsFilename.length > 0
        ? this.uploadedOnOffConsiderationsFilename
        : this.onAndOffConsiderationsFileInput.nativeElement.files[0]?.name,
      selectedObligateAndIndirectRelationshipFileName: this.uploadedObligateAndIndirectRelationshipFilename && this.uploadedObligateAndIndirectRelationshipFilename.length > 0
        ? this.uploadedObligateAndIndirectRelationshipFilename
        : this.obligateAndIndirectRelationshipFileInput.nativeElement.files[0]?.name
    };

    var modelInputs : ProjectXEpaXModelInputsDTO = {
      inputs: JSON.stringify(inputs),
      project_pk: 0, // set after we have the project saved and the pk is returned. The save function will pass on the value to be used.
      model_pk: this.modelService.modelPk
    }

    this.epaProjectInputsService.setSelectedEsaInputs(modelInputs);
    if (resetChangeDetection) this.epaProjectInputsService.resetChangeDetection();
  }

  async setFilesToBeUploaded() {
    var files = [this.pctValuesFileInput.nativeElement.files[0], this.onAndOffConsiderationsFileInput.nativeElement.files[0], this.obligateAndIndirectRelationshipFileInput.nativeElement.files[0]];
    var validFiles = files.filter(x => x != null);
    var filesToBeUploaded: File[]= validFiles;
    this.udlOverlapToolContainerService.setFilesToBeUploaded(filesToBeUploaded);
  }

  setSavedInputsOnSelectedProject() {
    this.selectedProjectApiService.selectedProject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (project) => {
      this.loading = true;
      if (!project || project?.projectPk < 0 || project?.name.length == 0) {
        this.resetDefaultModelInputs();
        return;
      }

      await this.getSavedModelInputs(project.projectPk);
    });
  }

  async getSavedModelInputs(selectedProjectPk: number) {
    this.epaProjectInputsService.getProjectEpaInputs(selectedProjectPk).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async inputsDto => {
      if (inputsDto === undefined || inputsDto.length == 0) return;
        var modelInputs = inputsDto[0].inputs
        if (modelInputs) {

          var inputs = JSON.parse(modelInputs) as EsaInputs;
          this.chemicalNameChanged(inputs.chemicalName);
          this.bufferDistancesChanged(this.controlledVocabularyService.bufferDistancesOptions.filter(x => inputs.selectedBufferDistances.some(y => y.udl_controlled_vocabulary_pk == x.udl_controlled_vocabulary_pk)));
          this.onOffFieldConsiderationsChanged(this.controlledVocabularyService.onOffFieldConsiderationsOptions.filter(x => inputs.selectedOnOffFieldConsiderations.some(y => y.udl_controlled_vocabulary_pk == x.udl_controlled_vocabulary_pk)));
          this.bufferDistanceInSummaryReportSelectionChanged(this.bufferDistanceInSummaryReportOptions.find(x => x.udl_controlled_vocabulary_pk == inputs.selectedBufferDistanceInSummaryReport.udl_controlled_vocabulary_pk));
          this.regionsToIncludeSelectionChanged(this.controlledVocabularyService.regionsToIncludeOptions.filter(x => inputs.selectedRegionsToInclude.some(y => y.udl_controlled_vocabulary_pk == x.udl_controlled_vocabulary_pk)));

          this.selectedStatesToInclude = this.controlledVocabularyService.statesToIncludeOptions.filter(x => inputs.selectedStatesToInclude.some(y => y.udl_controlled_vocabulary_pk == x.udl_controlled_vocabulary_pk));
          this.speciesDistributionSelectionChanged(this.controlledVocabularyService.speciesDistributionOptions.find(x => x.udl_controlled_vocabulary_pk == inputs.selectedSpeciesHabitat.udl_controlled_vocabulary_pk))
          this.includeDrift = inputs.includeDrift;
          this.adjustForPCT = inputs.adjustForPCT;
          this.isPCT = inputs.isPCT;

          this.onCropUseChanged(inputs.selectedCropUseValues);
          this.uploadedPCTFilename = inputs.selectedPCTValuesFileName;
          this.uploadedOnOffConsiderationsFilename = inputs.selectedOnOffConsiderationsFileName;
          this.uploadedObligateAndIndirectRelationshipFilename = inputs.selectedObligateAndIndirectRelationshipFileName;

          this.isPCTValuesFileValid = (inputs.selectedPCTValuesFileName != undefined && inputs.selectedPCTValuesFileName.length > 0);
          this.isOnAndOffConsiderationsFileValid = (inputs.selectedOnOffConsiderationsFileName != undefined && inputs.selectedOnOffConsiderationsFileName.length > 0);
          this.isObligateAndIndirectRelationshipFileValid = (inputs.selectedObligateAndIndirectRelationshipFileName != undefined && inputs.selectedObligateAndIndirectRelationshipFileName.length > 0);

          this.validateModelInputs();
          this.setupStatesToInclude();
          this.loading = false;
          await this.setModelInputs(true);
        }
    });
  }
}
